/**
 * Max Pallets helpers for the production `Stores.js` panel (hotfix only).
 *
 * This is a port of ENG-10281 (PR #3109) onto the pre-refactor Stores panel. Master carries the
 * same logic in `Stores/utils.js`; the next release from master replaces this file. Keep the
 * behavior identical: same column, same null-means-unset meaning, same normalization.
 */

/** Largest value a Postgres `integer` column holds. */
export const MAX_PALLETS_LIMIT = 2147483647;

/**
 * Turns the Max Pallets text input into a value that is safe to save.
 * Cuts the text at the first '.' so digits are never joined across a decimal separator
 * ('12.5' -> 12, '1,500.00' -> 1500, '.5' -> null), then keeps only digits. Returns `null` when
 * nothing is left, and clamps anything above the Postgres integer maximum down to it.
 * @param {string|number|null|undefined} input Raw text from the field.
 * @returns {number|null} A whole number from 0 to 2147483647, or null for "not set".
 */
export const normalizeMaxPallets = (input) => {
    const digits = String(input ?? '')
        .split('.')[0]
        .replace(/\D/g, '');
    if (!digits) return null;
    return Math.min(Number(digits), MAX_PALLETS_LIMIT);
};

/**
 * One saved location as the form shows it: the saved Max Pallets (from the separate query), then
 * any value whose write failed, then the user's staged edits. Both maps are keyed by `location_id`,
 * so they stay on the right store if a refetch reorders the list.
 * @param {{ location: object, override?: object, maxPallets?: object, pendingMaxPallets?: object }} args
 * @returns {object} The merged location.
 */
export const mergeSavedLocation = ({ location, override, maxPallets, pendingMaxPallets }) => {
    const from = (map, id) => (map && id in map ? { warehouse_max_pallets: map[id] } : {});
    return {
        ...location,
        ...from(maxPallets, location.location_id),
        ...from(pendingMaxPallets, location.location_id),
        ...override,
    };
};

/**
 * Normalizes Max Pallets on a location row headed for an insert or upsert, when the row has it.
 * @param {object} row Location row.
 * @returns {object} The row with `warehouse_max_pallets` as a whole number or null.
 */
export const withNormalizedMaxPallets = (row) =>
    'warehouse_max_pallets' in row
        ? { ...row, warehouse_max_pallets: normalizeMaxPallets(row.warehouse_max_pallets) }
        : row;

/**
 * Prepares a row for the `UPDATE_STORES` bulk upsert. Saved rows (with a `location_id`) drop
 * `warehouse_max_pallets`: the upsert must not depend on that column, and saved values are written
 * by `maxPalletsChanges` instead. A new row keeps and normalizes its typed value.
 * @param {object} row Location row.
 * @returns {object} The row for the upsert.
 */
export const upsertMaxPallets = (row) => {
    if (row.location_id) {
        const rest = { ...row };
        delete rest.warehouse_max_pallets;
        return rest;
    }
    return withNormalizedMaxPallets(row);
};

/**
 * The Max Pallets writes a save owes: one `{ location_id, warehouse_max_pallets }` per saved
 * location whose value the user edited or whose earlier write failed. Untouched locations are never
 * listed, so a save can't overwrite a stored value it didn't change.
 * @param {{ stores: object[], locationOverrides: object[], savedCount: number, pendingMaxPallets?: object }} args
 * @returns {{ location_id: string, warehouse_max_pallets: number|null }[]}
 */
export const maxPalletsChanges = ({ stores, locationOverrides, savedCount, pendingMaxPallets }) =>
    (stores || []).slice(0, savedCount).reduce((acc, store, idx) => {
        const override = (locationOverrides || [])[idx];
        const edited = !!override && 'warehouse_max_pallets' in override;
        const pending = store.location_id in (pendingMaxPallets || {});
        if (!store.location_id || !(edited || pending)) return acc;
        return [
            ...acc,
            { location_id: store.location_id, warehouse_max_pallets: normalizeMaxPallets(store.warehouse_max_pallets) },
        ];
    }, []);
